import {
  get,
  isString,
  isNil,
  find,
  isObject,
  isArray,
  last,
  map,
  set,
} from 'lodash';
import { Application, downloads } from '@integro/core';
import { FormatterEx, SlickGrid } from 'slickgrid';

export interface IAttachmentData {
  id: string;
  file_type: string;
  file_name: string;
  file_size: number;
}

export class AttachmentsHelper {
  app: Application;

  DEFAULT_FILE_TYPE_ICON = 'vscode-icons:file-type-binary';

  constructor(app: Application) {
    this.app = app;
  }

  downloadAttachmentFromSlickCommand(event: object) {
    const id = get(event, 'data.value');
    if (!isString(id)) {
      return;
    }

    const fieldName = get(event, 'data.column.field');
    if (!isString(fieldName)) {
      return;
    }

    const value = get(event, `data.data.${fieldName}`);

    if (!isNil(value) && Array.isArray(value)) {
      const item = find(value, { id }) as IAttachmentData;
      if (item) {
        return this.downloadAttachment(item);
      }
    } else if (isObject(value)) {
      const item = value as IAttachmentData;
      return this.downloadAttachment(item);
    }
  }

  downloadAttachment(data: IAttachmentData) {
    if (!data) {
      return;
    }

    const url = this.app.connections.urlFor('download_attachment', {
      id: data.id,
    });

    return Promise.resolve(downloads.getFileBLOB(url, data.file_name)).then(
      (p) => p && downloads.showFileInNewTab(p.file, p.fileName),
    );
  }

  downloadInvoiceView(invoiceId: string, invoiceNumber: string) {
    if (!invoiceId) {
      return;
    }

    const url = this.app.connections.urlFor('read_invoice_print', {
      id: invoiceId,
    });

    return Promise.resolve(
      downloads.getFileBLOB(url, `${invoiceNumber}.pdf`),
    ).then((p) => p && downloads.showFileInNewTab(p.file, p.fileName));
  }

  renderAttachmentElement(
    data: IAttachmentData,
    options?: {
      displayFileName?: boolean;
    },
  ) {
    let cellContent: string | null = get(data, 'cellContent', null);
    if (cellContent != null) {
      return cellContent;
    }

    const href = this.app.connections.urlFor('download_attachment', {
      id: data.id,
    });

    const iconClass = this.getIconClass(data);
    let content = '';

    if (options?.displayFileName) {
      content = `<span class="pl-2">${data.file_name}</span>`;
    }

    if (!iconClass) {
      cellContent = [
        `<a href="#" class="command-link"`,
        ` title="${data.file_name}"`,
        ` data-value="${data.id}"`,
        // ' class="badge badge-pill badge-default">',
        '>',
        `<i class="fa fa-file"></i>`,
        content,
        '</a>',
      ].join('');
    } else {
      cellContent = [
        `<a href="#" class="command-link"`,
        ` title="${data.file_name}"`,
        ` data-value="${data.id}"`,
        // ' class="badge badge-pill badge-default">',
        '>',
        `<iconify-icon icon="${iconClass}" noobserver></iconify-icon>`,
        content,
        '</a>',
      ].join('');
    }

    set(data, 'cellContent', cellContent);
    return cellContent;
  }

  getIconClass(data: IAttachmentData) {
    const extension = data.file_name.indexOf('.')
      ? last(data.file_name.split('.'))!.toLowerCase()
      : data.file_name.slice(1, 3);

    return get(
      this.app.config['fileTypeIcons'],
      extension,
      this.DEFAULT_FILE_TYPE_ICON,
    );
  }

  slickColumnFormatter() {
    const render = this.renderAttachmentElement.bind(this);

    return function attachmentColumnFormatter() {
      const attachmentFormatter: FormatterEx<any> = (
        row,
        cell,
        value,
        columnDef,
        dataContext,
        grid: SlickGrid,
      ) => {
        if (isArray(value)) {
          return `<div>${map(value, render).join(' ')}</div>`;
        }

        const displayFileName: boolean = get(
          columnDef,
          'displayFileName',
          false,
        );

        if (get(columnDef, 'useDataContext') && isObject(dataContext)) {
          console.log('useDataContext', dataContext);
          return render(dataContext as IAttachmentData, {
            displayFileName,
          });
        }

        if (isObject(value)) {
          return render(value as IAttachmentData, {
            displayFileName,
          });
        }

        return '';
      };

      set(attachmentFormatter, 'exportRawData', false);

      return attachmentFormatter;
    };
  }
}
